import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchClientFeedback } from "../../../store/slices/clientFeedbackSlice";
import { CLIENT_SECTIONS, LIKERT_LEGEND } from "../../../pages/ClientFeedback/clientFeedbackConfig";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const fmt = (v) => (v ? new Date(v).toLocaleDateString("en-GB") : "—");

export default function ClientFeedbackPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentClientFeedback, isLoading, error } = useSelector((s) => s.clientFeedback);

  useEffect(() => {
    if (id) dispatch(fetchClientFeedback(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentClientFeedback) {
      const t = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => { clearTimeout(t); window.onafterprint = null; };
    }
  }, [currentClientFeedback, navigate]);

  if (isLoading) return <div className="p-4 text-center">Loading Client Feedback print...</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentClientFeedback) return null;

  const d = currentClientFeedback;
  const mark = (key, n) => (Number(d[key]) === n ? "☑" : "☐");

  return (
    <>
      <style jsx global>{`
        body { margin: 0 !important; padding: 0 !important; }
        ${printFooterStyles}
        @media print {
          @page { size: A4; margin: 12mm 12mm; }
          body { background: #fff !important; color: #000 !important; }
          .no-print { display: none !important; }
          .section, table, tr { page-break-inside: avoid; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          table { border-collapse: collapse !important; }
          table, th, td { border: 1px solid #000 !important; }
          th, .section-head { background: #e9e9e9 !important; }
          .legend { background: #fafafa !important; border: 1px solid #000 !important; }
        }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .cf-print, .cf-print * { font-family: "Times New Roman", Times, serif; color: #000; }
        .cf-print { width: 100%; max-width: 820px; margin: 0 auto; padding: 16px; font-size: 10pt; line-height: 1.3; }
        .header-logo { text-align: center; margin-bottom: 4px; }
        .header-logo img { height: 58px; }
        .doc-title { text-align: center; font-size: 15pt; font-weight: bold; text-transform: uppercase; margin: 0 0 8px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
        td, th { border: 1px solid #000; padding: 3px 6px; font-size: 9pt; vertical-align: top; }
        th { background: #e9e9e9; text-align: center; }
        .lbl { font-weight: bold; background: #f7f7f7; width: 24%; }
        .section-head { font-weight: bold; font-size: 10pt; background: #e9e9e9; border: 1px solid #000; padding: 3px 6px; text-transform: uppercase; margin: 6px 0 4px; }
        .legend { border: 1px solid #000; padding: 4px 6px; font-size: 8.5pt; margin-bottom: 6px; background: #fafafa; }
        .notes-box { border: 1px solid #000; min-height: 38px; padding: 4px 6px; white-space: pre-wrap; font-size: 9pt; margin-bottom: 6px; }
        .qcol { text-align: left; }
        .mark { text-align: center; width: 7%; font-size: 11pt; }
        .footer { text-align: center; font-size: 8.5pt; margin-top: 10px; border-top: 1px solid #000; padding-top: 4px; }
      `}</style>

      <PrintWrapper>
        <div className="cf-print">
        <div className="header-logo"><img src={logo} alt="The Recovery House" /></div>
        <h1 className="doc-title">Client Satisfaction Report</h1>

        {/* Basic info */}
        <table>
          <tbody>
            <tr><td className="lbl">Client Name</td><td>{val(d.client_name)}</td><td className="lbl">Age</td><td>{val(d.age)}</td></tr>
            <tr><td className="lbl">Gender</td><td>{val(d.gender)}</td><td className="lbl">Interviewer Name</td><td>{val(d.interviewer_name)}</td></tr>
            <tr><td className="lbl">Date of Admission</td><td>{fmt(d.date_of_admission)}</td><td className="lbl">Date of Discharge</td><td>{fmt(d.date_of_discharge)}</td></tr>
          </tbody>
        </table>

        {/* Legend */}
        <div className="legend">
          <strong>Likert Scale Guide:</strong>{" "}
          {LIKERT_LEGEND.map((l) => `${l.n} = ${l.label}`).join("  |  ")}
        </div>

        {/* Sections */}
        {CLIENT_SECTIONS.map((section) => (
          <table key={section.title}>
            <thead>
              <tr>
                <th className="qcol" style={{ textAlign: "left" }}>{section.title}</th>
                <th style={{ width: "7%" }}>1</th>
                <th style={{ width: "7%" }}>2</th>
                <th style={{ width: "7%" }}>3</th>
                <th style={{ width: "7%" }}>4</th>
                <th style={{ width: "7%" }}>5</th>
              </tr>
            </thead>
            <tbody>
              {section.questions.map((q) => (
                <tr key={q.key}>
                  <td className="qcol">{q.label}</td>
                  <td className="mark">{mark(q.key, 1)}</td>
                  <td className="mark">{mark(q.key, 2)}</td>
                  <td className="mark">{mark(q.key, 3)}</td>
                  <td className="mark">{mark(q.key, 4)}</td>
                  <td className="mark">{mark(q.key, 5)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}

        {/* Additional feedback */}
        <div className="section-head">Section O: Additional Feedback</div>
        <div style={{ fontSize: "9pt", margin: "4px 0 3px" }}>Please share any suggestions or comments to help us improve:</div>
        <div className="notes-box">{d.additional_feedback || ""}</div>

        <div className="footer">CONFIDENTIAL — For Authorised Clinical Use Only</div>
      </div>
      </PrintWrapper>
    </>
  );
}
